import { type ResultOf } from '@graphql-typed-document-node/core';
import { graphql } from '@mp/graphql/generated';
import { useCurrentUser } from '@mp/hooks/useCurrentUser';
import { useGraphQLQuery } from '@mp/hooks/useGraphQLRequest';

export const EhrsGql = graphql(`
  query GetEhrsForResumeBuilder($input: EhrSystemQueryInput) {
    ehrSystems(input: $input) {
      id
      label
    }
  }
`);

export const EducationsGql = graphql(`
  query GetEducationsForResumeBuilder($input: EducationQueryInput) {
    educations(input: $input) {
      city
      degree_type
      end_date
      field_of_study
      id
      school_name
      start_date
      state_id
      ...EducationItem
    }
  }
`);

export const EducationItemFragment = graphql(`
  fragment EducationItem on Education {
    city
    degree_type
    end_date
    field_of_study
    id
    school_name
    start_date
    state_id
  }
`);

export const WorkHistoriesGql = graphql(`
  query GetWorkHistoriesForResumeBuilder($input: WorkHistoryQueryInput) {
    workHistories(input: $input) {
      id
      clinical
      currently_work_here
      description
      employment_type
      end_date
      floating
      floating_details
      hours_per_week
      location
      location_is_teaching
      location_number_of_beds
      location_trauma_level
      location_type_id
      missing_information_list
      organization_name
      position_id
      position_title
      start_date
      state_id
      travel_agency
      unit
      clinical
    }
  }
`);

export const WorkGapsGql = graphql(`
  query GetWorkGapsForResumeBuilder($input: WorkGapQueryInput) {
    workGaps(input: $input) {
      id
      start_date
      end_date
      explanation
      location
      state_id
      state {
        abbreviation
      }
    }
  }
`);

export const useEHRExperiencesForResumeBuilder = () => {
  const { currentUser } = useCurrentUser();
  const professional_id = currentUser?.professional?.id;
  const queryKey = ['ehrs', professional_id];

  const { data, isLoading } = useGraphQLQuery<ResultOf<typeof EhrsGql>>({
    operationName: 'GetEhrsForResumeBuilder',
    queryKey,
    query: EhrsGql,
    variables: {
      input: { search: { professional_id }, filter: { active: true }, orderBy: { name: true } },
    },
  });

  const ehrExperiences = data?.ehrSystems || [];

  return {
    isLoading,
    ehrExperiences,
  };
};

export const useEducationsForResumeBuilder = () => {
  const queryKey = ['educations'];

  const { data, isLoading } = useGraphQLQuery<ResultOf<typeof EducationsGql>>({
    operationName: 'GetEducationsForResumeBuilder',
    queryKey,
    query: EducationsGql,
    variables: {
      input: { orderBy: { end_date: false } },
    },
  });

  const educations = data?.educations || [];

  return {
    isLoading,
    educations,
  };
};

export const useWorkHistoriesForResumeBuilder = () => {
  const { data, isLoading } = useGraphQLQuery<ResultOf<typeof WorkHistoriesGql>>({
    operationName: 'GetWorkHistoriesForResumeBuilder',
    query: WorkHistoriesGql,
    variables: {
      input: { orderBy: { start_date: false } },
    },
  });

  const workHistories = data?.workHistories || [];

  return {
    isLoading,
    workHistories,
  };
};

export const useWorkGapsForResumeBuilder = () => {
  const { data, isLoading } = useGraphQLQuery<ResultOf<typeof WorkGapsGql>>({
    operationName: 'GetWorkGapsForResumeBuilder',
    query: WorkGapsGql,
    variables: {
      input: { orderBy: { start_date: false } },
    },
  });

  const workGaps = data?.workGaps || [];

  return {
    isLoading,
    workGaps,
  };
};
